/**
 * @file i18n.js
 * @description Internationalization (i18n) utility for Neptune web components.
 * Provides a centralized translation system for managing hardcoded UI text across components.
 *
 * Usage:
 *   import { i18n } from '/src/utils/i18n.js';
 *   const label = i18n.t('sidenav.collapse');
 *   i18n.setLocale('es'); // Change locale
 */

/**
 * I18n Manager class for translation functionality
 */
class I18nManager {
    constructor() {
        const globalCacheKey = '__NEPTUNE_I18N_CACHE__';
        const globalScope = typeof globalThis !== 'undefined' ? globalThis : {};
        if (!globalScope[globalCacheKey]) {
            globalScope[globalCacheKey] = {
                currentLocale: 'en-US',
                translations: {},
                fetchPromises: {},
            };
        }
        this.globalCache = globalScope[globalCacheKey];
        this.currentLocale = this.globalCache.currentLocale || 'en-US';
        this.translations = this.globalCache.translations;
        this.fallbackLocale = 'en-US';
        this.pendingLocale = null;
        this.fetchPromises = this.globalCache.fetchPromises;
        this.fetchLocale('en-US');
    }

    /**
     * Normalizes short locale aliases to canonical locale IDs used by locale files.
     * @param {string} locale
     * @returns {string}
     */
    normalizeLocale(locale) {
        if (!locale || typeof locale !== 'string') {
            return this.fallbackLocale;
        }
        const aliases = {
            en: 'en-US',
            es: 'es-ES',
            fr: 'fr-FR',
            de: 'de-DE',
            it: 'it-IT',
            ja: 'ja-JP',
            ko: 'ko-KR',
            pt: 'pt-PT',
            zh: 'zh-CN',
        };
        return aliases[locale] || locale;
    }

    /**
     * Get the current active locale
     * @returns {string} Current locale code (e.g., 'en', 'es', 'fr')
     */
    getLocale() {
        return this.currentLocale;
    }

    /**
     * Set the active locale
     * @param {string} locale - The locale code to set (e.g., 'en', 'es', 'fr')
     */
    setLocale(locale) {
        const normalizedLocale = this.normalizeLocale(locale);
        if (!this.translations[normalizedLocale]) {
            this.pendingLocale = normalizedLocale;
            this.fetchLocale(normalizedLocale);
            return;
        }
        this.pendingLocale = null;
        if (this.currentLocale === normalizedLocale) { return; }
        this.currentLocale = normalizedLocale;
        this.globalCache.currentLocale = normalizedLocale;
        this.emitLocaleChange();
    }

    /**
     * @description Fetch translations for a given locale from the server.
     * If the locale is not available, it will attempt to fetch from the fallback locale.
     * Once fetched, it registers the translations and emits a locale change event.
     * @param {*} locale 
        * @returns {Promise<void>|void}
     */
    fetchLocale(locale) {
        const normalizedLocale = this.normalizeLocale(locale);
        if (typeof fetch === 'undefined') { return; }
        if (this.fetchPromises[normalizedLocale]) { return; }

        const unresolvedLocaleBaseToken = '__LOCALE' + '_BASE__';
        const basePath = `__LOCALE_BASE__/${normalizedLocale}.json`;
        const paths = [];

        // When Storybook static is served from /storybook-static, absolute /locales/*
        // points to the server root and may 404. Prefer the subpath-local locales first.
        if (typeof window !== 'undefined' && window.location?.pathname?.includes('/storybook-static/')) {
            paths.push(`/storybook-static/locales/${normalizedLocale}.json`);
        }

        if (!basePath.includes(unresolvedLocaleBaseToken)) {
            paths.push(basePath);
        }
        paths.push(`/src/locales/${normalizedLocale}.json`);

        const tryNext = (i) => {
            if (i >= paths.length) { return Promise.resolve(); }
            return fetch(paths[i])
                .then(res => {
                    if (!res.ok) { throw new Error(); }
                    return res.json();
                })
                .then(data => { this.registerTranslations(normalizedLocale, data); })
                .catch(() => tryNext(i + 1));
        };
            this.fetchPromises[normalizedLocale] = tryNext(0);
    }

    /**
     * Register custom translations for a locale
     * Merges with existing translations to allow partial updates
     * @param {string} locale - The locale code (e.g., 'en', 'es')
     * @param {Object} translations - Translation object to merge
     */
    registerTranslations(locale, translations) {
        const normalizedLocale = this.normalizeLocale(locale);
        if (!this.translations[normalizedLocale]) {
            this.translations[normalizedLocale] = {};
        }
        this.translations[normalizedLocale] = this.deepMerge(
            this.translations[normalizedLocale],
            translations
        );
        if (this.pendingLocale === normalizedLocale) {
            this.pendingLocale = null;
            this.currentLocale = normalizedLocale;
            this.globalCache.currentLocale = normalizedLocale;
            this.emitLocaleChange();
        } else if (this.currentLocale === normalizedLocale) {
            this.emitLocaleChange();
        }
    }

    /**
     * Translate a key using dot notation
     * @param {string} key - Translation key using dot notation (e.g., 'sidenav.collapse')
     * @param {Object} [params={}] - Parameters for interpolation (e.g., { name: 'John' })
     * @returns {string} Translated string or the key if translation not found
     */
    translate(key, params = {}) {
        const locale = this.currentLocale;
        const keys = key.split('.');
        let value = this.translations[locale];

        // Navigate through nested object
        for (const k of keys) {
            value = value?.[k];
            if (!value) {
                // Try fallback locale
                value = this.translations[this.fallbackLocale];
                for (const fk of keys) {
                    value = value?.[fk];
                    if (!value) {
                        break;
                    }
                }
                if (!value) {
                    console.warn(`Neptune i18n Translation key "${key}" not found for locale "${locale}".`);
                    return key; // Return key if translation not found
                }
                break;
            }
        }

        // Handle string interpolation
        if (typeof value === 'string' && Object.keys(params).length > 0) {
            return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
                return params[paramKey] !== undefined ? params[paramKey] : match;
            });
        }

        return value || key;
    }

    /**
     * Get all translations for the current locale
     * @returns {Object} Full translation object for current locale
     */
    getAllTranslations() {
        return this.translations[this.currentLocale] || {};
    }

    /**
     * Get all available locales
     * @returns {string[]} Array of available locale codes
     */
    getAvailableLocales() {
        return Object.keys(this.translations);
    }

    /**
     * Subscribe to locale change events
     * @param {Function} callback - Function called when locale changes
     * @returns {Function} Unsubscribe function
     */
    onLocaleChange(callback) {
        if (!this.localeChangeCallbacks) {
            this.localeChangeCallbacks = [];
        }
        this.localeChangeCallbacks.push(callback);

        // Return unsubscribe function
        return () => {
            this.localeChangeCallbacks = this.localeChangeCallbacks.filter(cb => cb !== callback);
        };
    }

    /**
     * Emit locale change event to all subscribers
     * @private
     */
    emitLocaleChange() {
        if (this.localeChangeCallbacks) {
            this.localeChangeCallbacks.forEach(callback => {
                callback(this.currentLocale);
            });
        }
        // Dispatch custom event for DOM elements
        window.dispatchEvent(new CustomEvent('neptune-locale-change', {
            detail: { locale: this.currentLocale }
        }));
    }

    /**
     * @description Deep merge two objects (helper for registering translations)
     * @param {Object} target - The target object to merge into
     * @param {Object} source - The source object to merge from
     * @returns {Object} A new merged object
     * @private
     */
    deepMerge(target, source) {
        const result = { ...target };
        for (const key in source) {
            if (Object.prototype.hasOwnProperty.call(source, key)) {
                if (typeof source[key] === 'object' && !Array.isArray(source[key]) && source[key] !== null) {
                    result[key] = this.deepMerge(result[key] || {}, source[key]);
                } else {
                    result[key] = source[key];
                }
            }
        }
        return result;
    }
}

// Create singleton instance
export const i18n = new I18nManager();
export default i18n;
